import type { ScanEventDescription } from './fh-vitals-events'
import { SHENAI_FACE_STATE, SHENAI_MEASUREMENT_STATE } from './shenai-types'

/**
 * Shen's two state enums, mapped onto the same `ScanEventDescription` the other two vendors produce
 * — so one banner on one screen serves all three SDKs and a patient reads the same sentence
 * whichever vendor their scan happens to run.
 *
 * **Every key here already existed.** Not one new sentence was written for this vendor: Shen's
 * `FaceState` says too far, too close, not centred, not visible and turned away, and Mindset's
 * per-frame codes had already earned a sentence for each of those (docs/decisions.md D-28). That is
 * the whole argument for this app owning the patient's words rather than showing the vendor's: a
 * third SDK arrives and the patient-facing surface does not change at all.
 *
 * It names TRANSLATION KEYS and not sentences, for the reason fh-vitals-events.ts gives at length:
 * the scan surface localises from the token (architecture §9), and a literal here is a paragraph of
 * English in the middle of a Spanish page.
 *
 * Nothing here ends the session. Shen stops itself when it has what it needs, and the runner's own
 * window is what bounds a scan that does not - so `endsSession` is false throughout, exactly as it
 * is for Mindset.
 */
export function describeShenAiState(
  measurementState: number,
  faceState: number,
): ScanEventDescription[] {
  // Before the scan is running there is nothing to advise about: WAITING_FOR_FACE is the vendor
  // saying it has not seen a face yet, which the face signals below describe better than a state
  // name would.
  const described: ScanEventDescription[] = []
  const seen = new Set<string>()

  const push = (description: ScanEventDescription | null) => {
    if (!description) return
    const key = description.messageKey
    if (seen.has(key)) return
    seen.add(key)
    described.push(description)
  }

  push(describeFace(faceState))
  push(describeSignal(measurementState))

  return described
}

/**
 * Where the face is. Read on every tick, including while the signal is good, because a patient
 * drifting out of frame is the thing they can fix soonest.
 */
function describeFace(faceState: number): ScanEventDescription | null {
  switch (faceState) {
    case SHENAI_FACE_STATE.OK:
      return null
    case SHENAI_FACE_STATE.TOO_FAR:
      return { messageKey: 'faceTooFarDetail', endsSession: false }
    case SHENAI_FACE_STATE.TOO_CLOSE:
      return { messageKey: 'faceTooCloseDetail', endsSession: false }
    // Both land on one sentence: a face turned away is not a face the models can read, and "move so
    // your face sits inside the outline" is the instruction that fixes either - the guide drawn over
    // the preview is what the words point at. Mindset's three separate codes for "keep face
    // centered" are collapsed the same way (mindset-events.ts).
    case SHENAI_FACE_STATE.NOT_CENTERED:
    case SHENAI_FACE_STATE.TURNED_AWAY:
      return { messageKey: 'faceCentreDetail', endsSession: false }
    case SHENAI_FACE_STATE.NOT_VISIBLE:
      return { messageKey: 'faceLossDetail', endsSession: false }
    case SHENAI_FACE_STATE.UNKNOWN:
      // NOT "we cannot see your face". UNKNOWN is the vendor saying it has not decided yet, which is
      // what it reports for the first seconds of every session while its detector warms up - and it
      // is what a driven browser sees for the whole of a scan against a synthetic camera. Mapping it
      // onto the face-loss sentence would open every real scan by telling the patient something is
      // wrong. There is nothing to advise about an undetermined state, so nothing is said.
      return null
    default:
      // An enum member this app has not seen. Logged rather than shown, which is how the next one
      // gets found - and it is how UNKNOWN was found, in a driven browser, after the seam had been
      // written from a `.d.ts` whose members were miscounted by one.
      console.warn('[shenai] an unmapped face state, so the patient was told nothing', { faceState })
      return null
  }
}

/**
 * Why the signal is poor, when the vendor can distinguish it.
 *
 * `RUNNING_SIGNAL_BAD_DEVICE_UNSTABLE` is the one Shen says outright and the others do not: the
 * PHONE is moving, not the face. It gets "hold still", which is the sentence for exactly that.
 *
 * `RUNNING_SIGNAL_BAD` is deliberately NOT given a cause. The vendor does not say whether it is
 * lighting, motion or skin tone, and guessing "improve your lighting" at somebody sitting in a
 * bright room is the kind of confidently wrong instruction D-28 was written about. The face signals
 * above usually have something concrete to say at the same moment; when they do not, the screen
 * keeps its standing "hold still and look at the camera".
 */
function describeSignal(measurementState: number): ScanEventDescription | null {
  switch (measurementState) {
    case SHENAI_MEASUREMENT_STATE.RUNNING_SIGNAL_BAD_DEVICE_UNSTABLE:
      return { messageKey: 'motionDetail', endsSession: false }
    default:
      return null
  }
}
